import type { MeditationPauseMultiplier } from "../types";

import {
    estimateMeditationMinutes,
    MEDITATION_CATALOG,
} from "./meditation-catalog";

// AIDEV-NOTE: EVO-2476 — why a meditation's length showed up only sometimes.
//
// A catalog meditation (the part check-in and the rest) carries a hand-authored spokenMin /
// pauseMin, so the meditation picker and the pacing screen could always say "About N min".
// A RESOURCE exercise has neither — no DB column, nothing in its config — so an Academy practice
// ran its whole setup with no length anywhere, and the fullscreen player, which only prints a
// total once the HLS playlist finalizes, showed "0:07 / 19:45" on a cached replay and a bare
// "0:06" on a fresh generation. What the user saw tracked cache state, not the meditation.
//
// Partner practices (HWA, and the DPMP scripts written verbatim) carry their whole text and every
// [pause:N] under a **CANONICAL SCRIPT** marker, so their length is derivable before a single word
// is rendered. That is what this module does. A GENERATIVE template has no script until the model
// writes one, so its length is genuinely unknown ahead of the render and these functions return
// null rather than guessing — except where the catalog holds a hand-authored estimate for the same
// resource, which is a real number and beats showing nothing.

// AIDEV-NOTE: Keep in step with part-recall.ts, which splits the same templates on this marker.
const CANONICAL_SCRIPT_MARKER = "**CANONICAL SCRIPT**";
const PAUSE_MARKER = /\[pause:(\d+)\]/g;
// Substitution brackets ([[ the part by name ]], [[ recall:body ]]) are spoken as a short phrase.
const SUBSTITUTION_BRACKET = /\[\[[^\]]*\]\]/g;
const SUBSTITUTION_WORDS = 2;
const WORD = /[A-Za-z0-9’']+/g;

// AIDEV-NOTE: A `[[ recall:<key> ]]` bracket is NOT a two-word substitution like
// `[[ the part by name ]]`, and counting it as one understated these practices by minutes.
// When the selected part has the matching stored attribute, part-recall.ts replaces the bracket
// with an instruction that renders one or two sentences carrying THREE pauses of its own:
// [pause:2] after the let-out, [pause:3] before the invitation, and a closing hold (10s for
// body/fear, 12s for others — recallInstruction's `hold` argument). At 3x pacing those pauses
// triple, which is where the gap became several minutes.
const RECALL_BRACKET = /\[\[\s*recall:(\w+)\s*\]\]/g;
const RECALL_FIXED_PAUSE_SECONDS = 2 + 3;
const RECALL_HOLD_SECONDS: Record<string, number> = { others: 12 };
const RECALL_DEFAULT_HOLD_SECONDS = 10;
// "one or two sentences, and nothing more", in Tamala's register. A budget, not a measurement.
const RECALL_SPOKEN_WORDS = 35;
// AIDEV-NOTE: Mirrors MAX_RECALL_LINES_PER_SCRIPT in features/resources/server/part-recall.ts,
// which is server-side and must not be imported into the browser bundle. The unit test imports
// both and fails if they drift.
const MAX_RECALL_LINES = 3;

/**
 * What the recall brackets in a script add, once resolved.
 *
 * AIDEV-NOTE: This assumes every bracket RESOLVES, and that is a deliberate choice rather than an
 * oversight. A bracket only renders when the selected part holds the matching stored attribute,
 * and the browser does not carry those attributes, so the number cannot be known here. Assuming
 * they resolve makes the estimate run slightly long for a part with no stored attributes, and
 * slightly long is the safe direction for a number whose entire job is "do I have time for this
 * before my next thing".
 */
function recallExpansion(
    script: string,
    pauseMultiplier: number,
): { seconds: number; words: number } {
    const keys = [...script.matchAll(RECALL_BRACKET)]
        .map((match) => match[1])
        .slice(0, MAX_RECALL_LINES);
    const pauseSeconds = keys.reduce(
        (total, key) =>
            total +
            RECALL_FIXED_PAUSE_SECONDS +
            (RECALL_HOLD_SECONDS[key] ?? RECALL_DEFAULT_HOLD_SECONDS),
        0,
    );
    return {
        seconds: pauseSeconds * pauseMultiplier,
        words: keys.length * RECALL_SPOKEN_WORDS,
    };
}

/**
 * Spoken pace of the TTS voices on these scripts, in words per second.
 *
 * AIDEV-NOTE: Fitted against two independently measured renders, not guessed:
 *   - "Building a Trusting Relationship" runs 19:45 (1185s). Its script is 1081 words plus 711s
 *     of pauses, so 1081 / (1185 − 711) = 2.28.
 *   - "Coming into Relationship" rendered at 409s (recorded in the header of
 *     scripts/repace-hwa-practices-to-her-timing-20261013.sql). 351 words plus 256s of pauses
 *     gives 2.29.
 * Both land on 2.28, and the result is rounded to whole minutes before anyone sees it, so a few
 * percent of drift never reaches the screen. Re-fit this against a measured render rather than
 * nudging it to make one practice read better.
 *
 * AIDEV-NOTE: One caveat on that fit, so nobody treats 2.28 as measured to more precision than it
 * is. Neither render recorded whether its recall brackets RESOLVED, and a resolved bracket adds
 * both pauses and words (see recallExpansion). The fit therefore absorbs whatever recall those
 * two renders happened to carry. Since recall is now counted separately, a practice with three
 * resolved recalls reads about a minute longer than the 19:45 that calibrated this. That is
 * inside the rounding for every shipped practice at standard pacing, and the way to improve it
 * is to measure a render whose recall state is known — not to re-tune the constant.
 */
export const SCRIPTED_MEDITATION_WORDS_PER_SECOND = 2.28;

/**
 * Estimate the spoken length of a verbatim-script exercise, in seconds.
 *
 * Returns null for a generative template (no canonical script) — its length cannot be known
 * until the audio is rendered.
 */
export function estimateScriptedMeditationSeconds(
    promptTemplate: string | null | undefined,
    pauseMultiplier = 1,
): number | null {
    if (!promptTemplate) return null;
    const markerIndex = promptTemplate.indexOf(CANONICAL_SCRIPT_MARKER);
    if (markerIndex === -1) return null;

    const script = promptTemplate.slice(
        markerIndex + CANONICAL_SCRIPT_MARKER.length,
    );

    let pauseSeconds = 0;
    for (const match of script.matchAll(PAUSE_MARKER)) {
        pauseSeconds += Number(match[1]);
    }

    const recall = recallExpansion(script, pauseMultiplier);
    // Recall brackets are counted by recallExpansion, so they must not also be counted as
    // ordinary two-word substitutions.
    const recallBrackets = script.match(RECALL_BRACKET)?.length ?? 0;
    const substitutions =
        (script.match(SUBSTITUTION_BRACKET)?.length ?? 0) - recallBrackets;
    const spokenText = script
        .replace(PAUSE_MARKER, " ")
        .replace(SUBSTITUTION_BRACKET, " ");
    const words =
        (spokenText.match(WORD)?.length ?? 0) +
        substitutions * SUBSTITUTION_WORDS +
        recall.words;

    if (words === 0) return null;

    return Math.round(
        words / SCRIPTED_MEDITATION_WORDS_PER_SECOND +
            pauseSeconds * pauseMultiplier +
            recall.seconds,
    );
}

/**
 * The catalog's own estimate for a resource-backed meditation, in seconds.
 *
 * AIDEV-NOTE: A handful of resources double as catalog entries (the DPMP cards, "Getting to know
 * a protector"), and those entries carry a hand-authored spokenMin / pauseMin. Their templates are
 * generative, so the script estimate above returns null for them — this is the fallback that keeps
 * a real, authored number on screen instead of nothing.
 */
export function catalogMeditationSeconds(
    collectionSlug: string | undefined,
    resourceSlug: string | undefined,
    pauseMultiplier: MeditationPauseMultiplier = 1,
): number | null {
    if (!collectionSlug || !resourceSlug) return null;
    const item = MEDITATION_CATALOG.find(
        (entry) =>
            entry.backing.kind === "resource" &&
            entry.backing.collectionSlug === collectionSlug &&
            entry.backing.resourceSlug === resourceSlug,
    );
    if (!item) return null;
    return estimateMeditationMinutes(item, pauseMultiplier) * 60;
}

/**
 * Best available length for a resource exercise, in seconds, or null when nothing knows it.
 *
 * The verbatim script wins: it is derived from the words that will actually be spoken, where the
 * catalog number is a round figure typed by hand.
 */
export function estimateExerciseSeconds({
    promptTemplate,
    collectionSlug,
    resourceSlug,
    pauseMultiplier = 1,
}: {
    promptTemplate?: string | null;
    collectionSlug?: string;
    resourceSlug?: string;
    pauseMultiplier?: MeditationPauseMultiplier;
}): number | null {
    return (
        estimateScriptedMeditationSeconds(promptTemplate, pauseMultiplier) ??
        catalogMeditationSeconds(collectionSlug, resourceSlug, pauseMultiplier)
    );
}

/** "About 20 min" — the phrasing the meditation picker and pacing screens already use. */
export function formatApproximateLength(
    seconds: number | null | undefined,
): string | null {
    if (!seconds || seconds <= 0) return null;
    return `About ${Math.max(1, Math.round(seconds / 60))} min`;
}

/** "~20 min" — the compact form, for the player's readout beside the elapsed clock. */
export function formatCompactLength(
    seconds: number | null | undefined,
): string | null {
    if (!seconds || seconds <= 0) return null;
    return `~${Math.max(1, Math.round(seconds / 60))} min`;
}
